import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Settings.css';

interface SettingsProps {
  token: string;
  user: { id: string; email: string };
  onLogout: () => void;
  apiBaseUrl: string;
}

interface SettingsData {
  headScripts: string;
  footerScripts: string;
  adPlaceholder: string;
  blogHeaderBanner: string;
}

const Settings: React.FC<SettingsProps> = ({ token, user, onLogout, apiBaseUrl }) => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<SettingsData>({
    headScripts: '',
    footerScripts: '',
    adPlaceholder: '',
    blogHeaderBanner: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [bannerValidationError, setBannerValidationError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError('');

    try {
      const apiUrl = `${apiBaseUrl.replace('/api/public', '')}/api/settings`;
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const bannerValue = data.data?.blog_header_banner_base64;
        
        setSettings({
          headScripts: data.data?.head_scripts || '',
          footerScripts: data.data?.footer_scripts || '',
          adPlaceholder: data.data?.ad_placeholder || '',
          blogHeaderBanner: bannerValue || ''
        });
      } else if (response.status === 404) {
        // Settings don't exist yet, use defaults
        setSettings({
          headScripts: '',
          footerScripts: '',
          adPlaceholder: '',
          blogHeaderBanner: ''
        });
      } else {
        setError('Failed to fetch settings');
      }
    } catch (err) {
      console.error('Settings fetch error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateBase64Image = (input: string): boolean => {
    if (!input || input.trim() === '') {
      setBannerValidationError('');
      return true; // Empty is allowed
    }
    
    const trimmed = input.trim();
    // Check if it's a valid data URL format
    const dataUrlPattern = /^data:image\/(jpeg|jpg|png|gif|webp);base64,[A-Za-z0-9+/=]+$/;
    if (!dataUrlPattern.test(trimmed)) {
      setBannerValidationError('Invalid format. Must be a data URL like: data:image/jpeg;base64,/9j/4AAQSkZJRg...');
      return false;
    }
    
    // Check size limit: max 1MB for base64 data (approximately 1,333,333 characters)
    const MAX_BASE64_SIZE = 1333333;
    if (trimmed.length > MAX_BASE64_SIZE) {
      setBannerValidationError(`Image too large. Maximum size is 1MB (current: ${Math.round(trimmed.length / 1024)}KB)`);
      return false;
    }
    
    setBannerValidationError('');
    return true;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validate banner field in real-time
    if (name === 'blogHeaderBanner') {
      validateBase64Image(value);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setBannerValidationError('');

    // Validate banner before saving
    if (!validateBase64Image(settings.blogHeaderBanner)) {
      setSaving(false);
      return;
    }

    try {
      const apiUrl = `${apiBaseUrl.replace('/api/public', '')}/api/settings`;
      const payload = {
        head_scripts: settings.headScripts,
        footer_scripts: settings.footerScripts,
        ad_placeholder: settings.adPlaceholder,
        blog_header_banner_base64: settings.blogHeaderBanner
      };
      
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log('Save response status:', response.status);

      if (response.ok) {
        // Show success message
        const successMsg = document.createElement('div');
        successMsg.textContent = 'Settings saved successfully!';
        successMsg.className = 'success-message';
        document.body.appendChild(successMsg);
        setTimeout(() => {
          document.body.removeChild(successMsg);
        }, 2000);
      } else {
        const data = await response.json();
        console.error('Save error:', data);
        setError(data.message || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Save network error:', err);
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/cms');
  };

  if (loading) {
    return (
      <div className="settings">
        <header className="settings-header">
          <div className="header-content">
            <h1>SolveSmartHome CMS</h1>
            <div className="user-info">
              <span>Welcome, {user.email}</span>
              <button onClick={onLogout} className="logout-button">
                Logout
              </button>
            </div>
          </div>
        </header>
        
        <main className="settings-main">
          <div className="settings-content">
            <div className="loading">
              <div className="spinner"></div>
              <p>Loading settings...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="settings">
      <header className="settings-header">
        <div className="header-content">
          <h1>SolveSmartHome CMS</h1>
          <div className="user-info">
            <span>Welcome, {user.email}</span>
            <button onClick={onLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="settings-main">
        <div className="settings-content">
          <div className="settings-header-section">
            <div className="settings-title-section">
              <h2>Site Settings</h2>
              <p>Configure monetization and site-wide scripts</p>
            </div>
            
            <div className="settings-actions">
              <button onClick={handleBack} className="action-button secondary">
                ← Back to Dashboard
              </button>
              <button 
                onClick={handleSave} 
                className="action-button primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          <div className="settings-form">
            <div className="settings-section">
              <h3>Head Scripts</h3>
              <p className="section-description">
                Scripts that will be injected into the &lt;head&gt; section of your public blog. 
                Perfect for analytics, ad networks, and other tracking scripts.
              </p>
              <div className="form-group">
                <textarea
                  name="headScripts"
                  value={settings.headScripts}
                  onChange={handleInputChange}
                  placeholder="<!-- Google Analytics -->&#10;&lt;script async src=&quot;https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID&quot;&gt;&lt;/script&gt;&#10;&lt;script&gt;&#10;  window.dataLayer = window.dataLayer || [];&#10;  function gtag(){dataLayer.push(arguments);}&#10;  gtag('js', new Date());&#10;  gtag('config', 'GA_MEASUREMENT_ID');&#10;&lt;/script&gt;"
                  className="form-textarea code-textarea"
                  rows={8}
                />
              </div>
            </div>

            <div className="settings-section">
              <h3>Footer Scripts</h3>
              <p className="section-description">
                Scripts that will be injected before the closing &lt;/body&gt; tag. 
                Good for additional tracking or performance scripts.
              </p>
              <div className="form-group">
                <textarea
                  name="footerScripts"
                  value={settings.footerScripts}
                  onChange={handleInputChange}
                  placeholder="<!-- Additional footer scripts -->&#10;&lt;script&gt;&#10;  // Your footer scripts here&#10;&lt;/script&gt;"
                  className="form-textarea code-textarea"
                  rows={6}
                />
              </div>
            </div>

            <div className="settings-section">
              <h3>Ad Placeholder HTML</h3>
              <p className="section-description">
                HTML code for ad blocks that will be automatically inserted into posts. 
                If you want manual control, insert markers like <code>--AD--</code> (HTML comment), <code>[AD]</code>, <code>--AD_PLACEHOLDER--</code>, or <code>[AD_PLACEHOLDER]</code> anywhere in your markdown content.
              </p>
              <div className="form-group">
                <textarea
                  name="adPlaceholder"
                  value={settings.adPlaceholder}
                  onChange={handleInputChange}
                  placeholder="<!-- Ezoic Ad Placeholder -->&#10;&lt;div id=&quot;ezoic-pub-ad-placeholder-101&quot;&gt;&lt;/div&gt;&#10;&lt;script&gt;&#10;    ezstandalone.cmd.push(function () {&#10;        ezstandalone.showAds(101);&#10;    });&#10;&lt;/script&gt;"
                  className="form-textarea code-textarea"
                  rows={10}
                />
              </div>
            </div>

            <div className="settings-section">
              <h3>Blog Header Banner</h3>
              <p className="section-description">
                Paste a base64-encoded image data URL here to customize your blog homepage header. The site tagline will be displayed centered on the banner image. Convert your image to base64 using an online tool or command-line utility.
              </p>
              <div className="form-group">
                <textarea
                  name="blogHeaderBanner"
                  value={settings.blogHeaderBanner}
                  onChange={handleInputChange}
                  placeholder="data:image/jpeg;base64,/9j/4AAQSkZJRg..."
                  className="form-textarea code-textarea"
                  rows={6}
                />
                {bannerValidationError && (
                  <div className="validation-error" style={{ color: '#d32f2f', marginTop: '8px', fontSize: '14px' }}>
                    {bannerValidationError}
                  </div>
                )}
                {settings.blogHeaderBanner && !bannerValidationError && (
                  <div className="banner-preview" style={{ marginTop: '16px' }}>
                    <p style={{ marginBottom: '8px', fontWeight: '500' }}>Preview:</p>
                    <div style={{ 
                      border: '1px solid #ddd', 
                      borderRadius: '4px', 
                      overflow: 'hidden',
                      maxWidth: '100%',
                      backgroundColor: '#f5f5f5'
                    }}>
                      <img 
                        src={settings.blogHeaderBanner} 
                        alt="Banner preview" 
                        style={{ 
                          width: '100%', 
                          height: 'auto', 
                          display: 'block',
                          maxHeight: '300px',
                          objectFit: 'contain'
                        }}
                        onError={() => {
                          setBannerValidationError('Invalid image data. Please check your base64 string.');
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="settings-info">
              <h4>How to Use</h4>
              <ul>
                <li><strong>Head Scripts:</strong> Add analytics, ad network initialization, or other scripts that need to load early</li>
                <li><strong>Footer Scripts:</strong> Add performance monitoring, additional tracking, or scripts that can load after page content</li>
                <li><strong>Ad Placeholder - Automatic:</strong> Ads are automatically inserted after every 3rd paragraph in your posts</li>
                <li><strong>Ad Placeholder - Manual:</strong> For precise control, insert <code>--AD--</code> (HTML comment: <code>&lt;!--AD--&gt;</code>), <code>[AD]</code>, <code>--AD_PLACEHOLDER--</code> (HTML comment: <code>&lt;!--AD_PLACEHOLDER--&gt;</code>), or <code>[AD_PLACEHOLDER]</code> anywhere in your markdown content. The system will replace these markers with the ad placeholder HTML. When manual markers are detected, automatic insertion is disabled for that post.</li>
                <li><strong>Blog Header Banner:</strong> Paste a base64-encoded image data URL (format: <code>data:image/[type];base64,[data]</code>). The image will display on your blog homepage with the site tagline centered on it. Maximum size: 1MB. Leave empty to use the default text-only header.</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;
